import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Navbar from "@/components/Navbar";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import AgentDashboard from "@/pages/AgentDashboard";
import OnboardingStart from "@/pages/OnboardingStart";
import RequestForm from "@/pages/RequestForm";
import ProjectDetail from "@/pages/ProjectDetail";
import Profile from "@/pages/Profile";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProjectDetail from "@/pages/AdminProjectDetail";
import AdminSettings from "@/pages/AdminSettings";
import AdminStats from "@/pages/AdminStats";
import AdminUsers from "@/pages/AdminUsers";
import AdminFileshare from "@/pages/AdminFileshare";
import AdminTools from "@/pages/AdminTools";
import AdminToolPhotoRetouch from "@/pages/AdminToolPhotoRetouch";
import AdminToolUpscale from "@/pages/AdminToolUpscale";
import AdminToolOutlineBlur from "@/pages/AdminToolOutlineBlur";
import AdminToolVideoTour from "@/pages/AdminToolVideoTour";
import MyInvoices from "@/pages/MyInvoices";
import AdminMails from "@/pages/AdminMails";
import AdminMailSettings from "@/pages/AdminMailSettings";
import AdminSystemMails from "@/pages/AdminSystemMails";
import AdminRetention from "@/pages/AdminRetention";
import AdminVisitors from "@/pages/AdminVisitors";
import TryLanding from "@/pages/TryLanding";
import AdminLeads from "@/pages/AdminLeads";
import Unsubscribe from "@/pages/Unsubscribe";
import AdminUserDetail from "@/pages/AdminUserDetail";
import PublicShare from "@/pages/PublicShare";
import PublicPreview from "@/pages/PublicPreview";
import PublicProject from "@/pages/PublicProject";
import PublicVideo from "@/pages/PublicVideo";
import PublicDemoGallery from "@/pages/PublicDemoGallery";
import WelcomeForLead from "@/pages/WelcomeForLead";
import VideoLanding from "@/pages/VideoLanding";
import AgendaPage from "@/pages/AgendaPage";
import ScriptReader from "@/pages/ScriptReader";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import CookieBanner from "@/components/CookieBanner";

function Shell({ children, withNavbar = true }) {
    return (
        <>
            {withNavbar && <Navbar />}
            {children}
        </>
    );
}

function LandingRoute() {
    // Keep landing available to everyone (incl. logged-in)
    const { user, loading } = useAuth();
    if (loading) return null;
    return (
        <Shell>
            <Landing />
        </Shell>
    );
}

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
};

const CaptureUtm = () => {
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const utm = {};
            ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "oppref"].forEach((k) => {
                const v = params.get(k);
                if (v) utm[k] = v.slice(0, 120);
            });
            if (Object.keys(utm).length && !localStorage.getItem("signup_utm")) {
                if (document.referrer) utm.referrer = document.referrer.slice(0, 200);
                localStorage.setItem("signup_utm", JSON.stringify(utm));
            }
        } catch (e) { /* noop */ }
    }, []);
    return null;
};

const TrackVisit = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        if (pathname.startsWith("/admin")) return;
        try {
            let sid = sessionStorage.getItem("vsf_sid");
            const isNew = !sid;
            if (!sid) {
                sid = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
                sessionStorage.setItem("vsf_sid", sid);
            }
            const params = new URLSearchParams(window.location.search);
            const stored = JSON.parse(localStorage.getItem("signup_utm") || "null") || {};
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                keepalive: true,
                body: JSON.stringify({
                    session_id: sid,
                    path: pathname,
                    utm_source: params.get("utm_source") || stored.utm_source || null,
                    utm_medium: params.get("utm_medium") || stored.utm_medium || null,
                    utm_campaign: params.get("utm_campaign") || stored.utm_campaign || null,
                    referrer: isNew ? (document.referrer || null) : null,
                    is_new_session: isNew,
                }),
            }).catch(() => {});
        } catch (e) { /* noop */ }
    }, [pathname]);
    return null;
};

function App() {
    return (
        <div className="App">
            <ErrorBoundary>
                <AuthProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <CaptureUtm />
                        <TrackVisit />
                    <Routes>
                        <Route path="/" element={<LandingRoute />} />
                        <Route path="/probeer" element={<TryLanding />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
                        <Route path="/demo/:token" element={<PublicDemoGallery />} />
                        <Route path="/welkom/:mailId" element={<WelcomeForLead />} />
                        <Route path="/video/:slug" element={<VideoLanding />} />
                        <Route path="/v/:token" element={<PublicVideo />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/voorwaarden" element={<Terms />} />
                        <Route path="/cookies" element={<CookiePolicy />} />

                        {/* Agent routes */}
                        <Route
                            path="/start"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <OnboardingStart />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <AgentDashboard />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/catalog"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <Landing />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/new-request"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <RequestForm />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:id"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <ProjectDetail />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/projects/:id/script"
                            element={
                                <ProtectedRoute>
                                    <ScriptReader />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/profile"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <Profile />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/agenda"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <AgendaPage />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />

                        {/* Admin routes */}
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminDashboard />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/projects"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminDashboard />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/projects/:id"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminProjectDetail />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/settings"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminSettings />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/stats"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminStats />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/calendar"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AgendaPage />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/users"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminUsers />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/fileshare"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminFileshare />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/tools"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminTools />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/tools/photo-retouch"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminToolPhotoRetouch />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/tools/upscale"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminToolUpscale />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/tools/omlijnen-blurren"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminToolOutlineBlur />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/tools/video-tour"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminToolVideoTour />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        {/* Makelaar / MM-facing tools — backend enforces tool_access */}
                        <Route
                            path="/tools"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <AdminTools />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/tools/photo-retouch"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <AdminToolPhotoRetouch />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/tools/upscale"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <AdminToolUpscale />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/tools/omlijnen-blurren"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <AdminToolOutlineBlur />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/tools/video-tour"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <AdminToolVideoTour />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/tools/invoices"
                            element={
                                <ProtectedRoute>
                                    <Shell>
                                        <MyInvoices />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/mails"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminMails />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/mail-settings"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminMailSettings />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/mail-templates"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminSystemMails />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/retention"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminRetention />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/visitors"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminVisitors />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/leads"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminLeads />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/users/:userId"
                            element={
                                <ProtectedRoute adminOnly>
                                    <Shell>
                                        <AdminUserDetail />
                                    </Shell>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/share/:slug"
                            element={
                                <Shell withNavbar={false}>
                                    <PublicShare />
                                </Shell>
                            }
                        />
                        <Route
                            path="/preview/:token"
                            element={
                                <Shell withNavbar={false}>
                                    <PublicPreview />
                                </Shell>
                            }
                        />
                        <Route
                            path="/project/:token"
                            element={
                                <Shell withNavbar={false}>
                                    <PublicProject />
                                </Shell>
                            }
                        />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <CookieBanner />
                    <Toaster
                        position="top-right"
                        theme="light"
                        toastOptions={{
                            style: {
                                background: "#FFFFFF",
                                border: "1px solid #E4E4E7",
                                color: "#09090B",
                                borderRadius: "6px",
                                boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                            },
                        }}
                    />
                </BrowserRouter>
            </AuthProvider>
            </ErrorBoundary>
        </div>
    );
}

export default App;
